import { useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';

// Contagem de visualizações da página do evento no site (webViews /
// webViewsUnique no Firestore, separados das views do app). Quem soma é a
// callable registerWebView (functions/registerWebView.js) — o navegador só
// avisa "abri este evento" e se é a primeira vez dele nesse evento.
//
// - webViews: 1x por evento por aba (sessionStorage) — recarregar ou voltar
//   pra página não conta de novo.
// - webViewsUnique: 1x por evento por navegador. A marca no localStorage é só
//   a lista de ids de eventos já vistos com a data — nenhum identificador do
//   visitante é criado nem enviado (ver seção 9 da Política de Privacidade).
//
// Robôs de busca/redes sociais recebem do renderForBots uma página sem React,
// então nunca chegam aqui; navegador automatizado (navigator.webdriver) é
// ignorado. Em desenvolvimento não conta, pra não somar visitas de teste.

const SESSION_KEY = 'joyn_web_views_session';
const SEEN_KEY = 'joyn_seen_events';
const SEEN_MAX = 500;
const SEEN_TTL_MS = 365 * 24 * 60 * 60 * 1000;

const registerWebViewCallable = httpsCallable(functions, 'registerWebView');

// Mesmo carregamento da página (StrictMode, redirecionamento de slug).
const countedThisLoad = new Set();

function readJson(storage, key, fallback) {
  try {
    const parsed = JSON.parse(storage.getItem(key));
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // storage indisponível (aba anônima restrita, cota cheia) — segue sem marca
  }
}

function markSessionView(eventId) {
  const ids = readJson(sessionStorage, SESSION_KEY, []);
  if (!Array.isArray(ids)) return true;
  if (ids.includes(eventId)) return false;
  writeJson(sessionStorage, SESSION_KEY, [...ids, eventId].slice(-SEEN_MAX));
  return true;
}

// { [eventId]: timestamp } sem as entradas vencidas.
function readSeen() {
  const seen = readJson(localStorage, SEEN_KEY, {});
  const now = Date.now();
  return Object.fromEntries(
    Object.entries(seen).filter(([, ts]) => typeof ts === 'number' && now - ts < SEEN_TTL_MS),
  );
}

function markSeen(eventId) {
  const entries = Object.entries({ ...readSeen(), [eventId]: Date.now() })
    .sort((a, b) => b[1] - a[1])
    .slice(0, SEEN_MAX);
  writeJson(localStorage, SEEN_KEY, Object.fromEntries(entries));
}

export function useWebViewCounter(event, id) {
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    // Só o evento da URL, depois de carregado (ao trocar de evento, o
    // anterior pode ainda estar em memória).
    if (!event || event.id !== id) return;
    if (countedThisLoad.has(id)) return;
    countedThisLoad.add(id);
    if (navigator.webdriver) return;
    if (!markSessionView(id)) return;

    const unique = !(id in readSeen());
    registerWebViewCallable({ eventId: id, unique })
      .then(({ data }) => {
        // Se não contou (limite do servidor), tenta o "único" numa próxima visita.
        if (unique && data?.counted) markSeen(id);
      })
      .catch(() => {
        // contador nunca pode atrapalhar a página
      });
  }, [event, id]);
}
